// Helpers that turn a search form's values into the filter analytics events of
// ZAN-897. The filter forms apply every dimension at once, so the events are
// derived by comparing the values that were applied with the previous ones,
// rather than by instrumenting each of the ~20 filter components.

export type FilterValues = Record<string, unknown>

export type FilterChange = { filter_type: string; filter_value: string }

const isEmpty = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === "" ||
  value === false ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === "object" &&
    !Array.isArray(value) &&
    Object.values(value as object).every((v) => !v))

const formatValue = (value: unknown): string => {
  if (Array.isArray(value)) return value.join(", ")
  if (typeof value === "object" && value !== null) {
    return Object.values(value as object)
      .filter(Boolean)
      .join(" - ")
  }
  return String(value)
}

/** Maps a roster search field to the filter dimension it belongs to. */
export const influencerFilterType = (field: string): string | null => {
  const exact: Record<string, string> = {
    creatorTypes: "creator_type",
    categories: "category",
    tagIds: "tags",
    countryCodes: "country",
    audienceCountryCodes: "audience_country",
    genders: "gender",
    audienceGenders: "audience_gender",
    ageMin: "age",
    ageMax: "age",
    audienceAges: "audience_age",
    collaborationTypes: "collaboration_type",
    languages: "language",
    exclusivities: "exclusivity",
    agencyIds: "agency",
  }

  if (exact[field]) return exact[field]
  if (/Enabled$/.test(field)) return "platform"
  if (/(HasRates|RateCents(Min|Max))$/.test(field)) return "rates"
  if (/(Followers|Subscribers)(Min|Max)$/.test(field)) return "followers"
  if (/(AvgViews|AvgStreamViews|ConcurrentViewers)(Min|Max)$/.test(field)) {
    return "avg_views"
  }
  if (/EngagementRate(Min|Max)$/.test(field)) return "engagement_rate"
  // `query` is reported as search_influencers instead.
  return null
}

/** Maps a casting call search field to the filter dimension it belongs to. */
export const castingCallFilterType = (field: string): string | null => {
  const exact: Record<string, string> = {
    collaborationTypes: "collaboration_type",
    postDate: "post_date",
    influencerCountryCodes: "country",
    influencerCategories: "category",
    influencerGenders: "gender",
    influencerLanguages: "language",
    postedByBusinessTypes: "posted_by",
  }

  return exact[field] ?? null
}

const sameValue = (a: unknown, b: unknown) =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null)

/**
 * Lists the filter dimensions that gained a value between two applied searches.
 * Dimensions that were only cleared are left out — a cleared search reports
 * clear_filters instead.
 */
export const changedFilters = (
  previous: FilterValues,
  next: FilterValues,
  filterType: (field: string) => string | null
): FilterChange[] => {
  const valuesByType = new Map<string, string[]>()

  for (const field of new Set([
    ...Object.keys(previous),
    ...Object.keys(next),
  ])) {
    if (sameValue(previous[field], next[field])) continue

    const type = filterType(field)
    if (!type || isEmpty(next[field])) continue

    valuesByType.set(type, [
      ...(valuesByType.get(type) ?? []),
      formatValue(next[field]),
    ])
  }

  return [...valuesByType.entries()].map(([filter_type, values]) => ({
    filter_type,
    filter_value: values.join("; "),
  }))
}

/** The filter dimensions that currently hold a value. */
export const activeFilterTypes = (
  values: FilterValues,
  filterType: (field: string) => string | null
): string[] => {
  const types = new Set<string>()

  for (const [field, value] of Object.entries(values)) {
    const type = filterType(field)
    if (type && !isEmpty(value)) types.add(type)
  }

  return [...types]
}
